import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useAlerts } from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";

/**
 * Headless app-wide announcer. Mounted once in <AppShell> so new alerts
 * are read aloud (when TTS is enabled) on every page, not just the alerts
 * view.
 *
 * Source of truth is the same `useAlerts` query the rest of the app uses
 * (polled every 30 s); we additionally subscribe to the org alert channel
 * to invalidate that query the instant a new alert fires — identical to
 * AlertsPage — so the announcement is near-real-time without a second
 * data path. Dedupe lives in `useAlertTTS` (per alert id), so re-renders
 * and overlapping mounts never double-speak.
 */
export function AlertTtsAnnouncer() {
  const { announce } = useAlertTTS();
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;

  const { data: alertsData } = useAlerts();
  const { data: sitesData } = useSites();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string) => m.get(Number(id)) ?? "";
  }, [sitesData]);

  // Push new alerts into the announcer whenever the list changes.
  const alerts = alertsData?.results ?? [];
  useEffect(() => {
    if (!alerts.length) return;
    announce(
      alerts.map((a) => ({ id: a.id!, level: a.level ?? 0, site: a.site, gas: a.gas })),
      siteName,
    );
  }, [alerts, announce, siteName]);

  // Refresh the alert list the moment a new alert fires server-side.
  useAlertChannel(orgId, (env) => {
    if (env.type === "alert.fired" || env.type === "alert.cleared") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  return null;
}
